import { CardControl } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/CardControl';
import { QuickExpand } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickExpand';
import { QuickSave } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickSave';
import { TextValuePreview } from 'components/detail/edit/TextValuePreview';
import { GradientLine } from 'components/detail/minute/GradientLine';
import { IConfirmActionModal } from 'components/site/modals/ModalWrapper/ModalContent/contents/ConfirmActionModal';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { ActionButton as ActionButton2 } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { TextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/TextValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSectionHeader } from 'elements/minute/sidebar/sidebarSection/SidebarSection/SidebarSectionHeader';
import { motion } from 'framer-motion';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EHovers } from 'libs/styles/styling';
import { useContext, useEffect, useState } from 'react';
import { CtxDetail } from 'session/ctxDetail';
import { CtxModal } from 'session/ctxModal';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    position: number;
    className?: string;
}

export const EditLinkCard = (props: Props): JSX.Element => {
    const ctxDetail = useContext(CtxDetail);
    const card = ctxDetail.updateDetailInput.cards[props.position];
    const [links, setLinks] = useState(card.links);
    const isUpdatingIndex = ctxDetail.updatingIndex === props.position;
    const ctxModal = useContext(CtxModal);

    useEffect(() => {
        if (card.links?.length === 0) {
            addLink();
        }
    }, []);

    function changeTitleOrUrl(index: number, type: 'title' | 'url', value: string) {
        const newLinks = [...links];
        newLinks[index][type] = value;
        setLinks(newLinks);
        ctxDetail.updateCardByPosition(
            {
                links: newLinks
            },
            props.position
        );
    }

    function deleteLink(index: number) {
        ctxModal.setModal({
            type: EModal.CONFIRM,
            data: {
                text: Localization.CONFIRM_DELETE_LINK,
                onConfirm: () => {
                    const newLinks = [...links];
                    newLinks.splice(index, 1);
                    setLinks(newLinks);
                    ctxDetail.updateCardByPosition(
                        {
                            links: newLinks
                        },
                        props.position
                    );
                }
            } as IConfirmActionModal
        });
    }

    function addLink() {
        const newLinks = [...links];
        newLinks.push({ title: '', url: '' });
        setLinks(newLinks);
    }

    return (
        <motion.div {...conditionalScaleAnimation(true, 1.02, 1.02)}>
            <S_MinuteCard
                className={props.className}
                $isUpdatingIndex={isUpdatingIndex}
                onClick={() => {
                    if (!isUpdatingIndex) {
                        ctxDetail.setUpdatingIndex(props.position);
                    }
                }}
            >
                <S_SidebarSectionHeader
                    $isUpdatingIndex={isUpdatingIndex}
                    icon={{
                        iconName: 'link'
                    }}
                    title={Localization.LINK}
                    highlighted={!!isUpdatingIndex}
                    topBorder
                >
                    {isUpdatingIndex && <QuickSave />}
                    {!isUpdatingIndex && <QuickExpand />}
                </S_SidebarSectionHeader>
                {!isUpdatingIndex && (
                    <S_Content $isUpdatingIndex={isUpdatingIndex}>
                        {links.map((link) => {
                            return (
                                <S_TextValuePreview
                                    key={link.url + link.title}
                                    value={link.title}
                                />
                            );
                        })}
                    </S_Content>
                )}
                {isUpdatingIndex && (
                    <>
                        <S_Subsection>
                            <SidebarContent noPadding>
                                {links.map((link, index) => {
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <S_MinuteLinkRow key={index}>
                                            <S_Left>
                                                <S_TextValue2
                                                    value={link.title}
                                                    onChange={(value) =>
                                                        changeTitleOrUrl(index, 'title', value)
                                                    }
                                                    placeholder={Localization.TITLE}
                                                    textAlign="left"
                                                    style={{ margin: '0 0 5px 0' }}
                                                />
                                                <S_TextValue2
                                                    value={link.url}
                                                    onChange={(value) =>
                                                        changeTitleOrUrl(index, 'url', value)
                                                    }
                                                    placeholder="URL"
                                                    textAlign="left"
                                                />
                                            </S_Left>
                                            <S_Right>
                                                {isUpdatingIndex && (
                                                    <S_MinuteIcon
                                                        onClick={() => {
                                                            deleteLink(index);
                                                        }}
                                                        icon={{ iconName: 'trash' }}
                                                    />
                                                )}
                                            </S_Right>
                                        </S_MinuteLinkRow>
                                    );
                                })}
                            </SidebarContent>
                        </S_Subsection>
                        <S_Add>
                            <ActionButton2
                                icon={{ iconName: 'plus' }}
                                text={
                                    links?.length === 0
                                        ? Localization.ADD_LINK
                                        : Localization.ADD_ANOTHER_LINK
                                }
                                onClick={addLink}
                            />
                        </S_Add>

                        <CardControl position={props.position} />
                        <S_GradientLine />
                    </>
                )}
            </S_MinuteCard>
        </motion.div>
    );
};

const S_Left = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0 15px 0 0;
    box-sizing: border-box;
`;

const S_Right = styled.div``;

const S_MinuteCard = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
    border-right: 1px solid #d3d3d3;
    border-left: 1px solid #d3d3d3;
    ${(props) => {
        if (props.$isUpdatingIndex) {
            return css`
                border-right: 1px solid #8f3a19;
                border-left: 1px solid #b19052;
            `;
        }
    }}
    ${(props) => {
        if (!props.$isUpdatingIndex) {
            return css`
                &:hover {
                    ${S_SidebarSectionHeader} {
                        box-shadow: ${EHovers.DARK1};
                    }
                    ${S_Content} {
                        box-shadow: ${EHovers.LIGHT4};
                        cursor: pointer;
                    }
                }
            `;
        }
    }}
`;

const S_SidebarSectionHeader = styled(SidebarSectionHeader)<{ $isUpdatingIndex: boolean }>`
    cursor: ${(props) => (props.$isUpdatingIndex ? 'unset' : 'pointer')};
`;

const S_Content = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
`;

const S_TextValuePreview = styled(TextValuePreview)``;

const S_Add = styled.div`
    display: flex;
    justify-content: center;
    padding: 15px 0;
    border-bottom: 1px solid #d3d3d3;
`;

const S_TextValue2 = styled(TextValue)``;

const S_GradientLine = styled(GradientLine)`
    margin: 15px 0 0 0;
`;

const S_MinuteLinkRow = styled.div`
    display: flex;
    border-bottom: 1px solid #d3d3d3;
    padding: 10px;
    &:hover {
        box-shadow: ${EHovers.LIGHT4};
    }
`;

const S_Subsection = styled.div`
    display: flex;
`;

const S_MinuteIcon = styled(ActionButton2)`
    margin: 10px 0px 0 0px;
`;
